import { Box, ThemeProvider } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';

import { HauserWordmark } from '@/components/dashboard/SidebarShell';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Divider,
  Eyebrow,
  Icon,
  IconButton,
  Meta,
  NavItem,
  NavSection,
  Segmented,
  Stat,
  StatRow,
  TextInput,
  Title,
} from '@/components/ds';
import { createAppTheme, ThemeName, tx } from '@/theme';

// /dev/compare: the same Hauser dashboard (its real layout: left rail, main pane, Ask Hauser panel) in three
// colour directions, for choosing Hauser's theme model. Development builds only.
//   two-dark   chalk ground, every surface slate (the original Hauser look, on the new components)
//   two-light  Tradehaus Natural throughout
//   materials  concrete ground, slate tools (rail, Ask Hauser), paper content (main pane)


const CHALK = '#BBB7AF';
const CHALK_INK_MUTED = 'rgba(43, 42, 40, 0.62)';

/* eslint-disable max-len */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
/* eslint-enable max-len */

// Hauser's own status colours, on marks only
const URGENCY = { now: '#E47192', soon: '#E68A6B', later: '#EBC064', ok: '#B5D274' };

const Themed = ({ name, vars, children }: { name: ThemeName; vars?: Record<string, string>; children: ReactNode }) => {
  const theme = useMemo(() => createAppTheme(name), [name]);
  return (
    <ThemeProvider theme={theme}>
      <Box data-theme={name} sx={{ display: 'contents', ...vars }}>
        {children}
      </Box>
    </ThemeProvider>
  );
};

const panel = (extra: object = {}) => ({
  backgroundColor: tx.panel,
  backgroundImage: tx.surfaceLight,
  backdropFilter: tx.blurPanel,
  WebkitBackdropFilter: tx.blurPanel,
  boxShadow: tx.shadow,
  borderRadius: `${tx.rSheet}px`,
  color: tx.text,
  ...extra,
});

const Rail = ({ drop, mode }: { drop: string; mode?: 'day' | 'night' }) => {
  const [active, setActive] = useState('condition');
  return (
    <Box
      sx={panel({
        width: 248,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        p: '18px 12px 12px',
        boxShadow: drop,
      })}>
      <Box sx={{ px: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: tx.text }}>
        <HauserWordmark height={22} color="currentColor" />
        <IconButton icon="sidebar" label="Collapse" size="sm" variant="ghost" tipPlacement={false} />
      </Box>
      <Box
        sx={{
          mx: '2px',
          p: '10px 12px',
          borderRadius: `${tx.rTile}px`,
          backgroundColor: tx.fill,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            flex: 'none',
            background: 'linear-gradient(135deg, #8a7f70, #4d463e)',
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>1234 Lita Place</Box>
          <Meta>Phoenix, AZ · In escrow</Meta>
        </Box>
        <Icon name="unfold" size={14} style={{ color: 'var(--tx-faint)' }} />
      </Box>
      <NavSection label="Due diligence">
        {(
          [
            ['summary', 'Summary', 'dash'],
            ['condition', 'Condition', 'home'],
            ['context', 'Context', 'pin'],
            ['disclosures', 'Disclosures', 'note'],
            ['report', 'Inspection report', 'book'],
            ['repair', 'Repair planner', 'pen'],
            ['market', 'Market', 'perform'],
          ] as const
        ).map(([k, l, i]) => (
          <NavItem
            key={k}
            label={l}
            icon={<Icon name={i} size={16} />}
            active={active === k}
            badge={k === 'repair' ? 2 : undefined}
            onClick={() => setActive(k)}
          />
        ))}
      </NavSection>
      <NavSection label="Tools">
        <NavItem label="Inbox" icon={<Icon name="mail" size={16} />} badge={3} />
        <NavItem label="Calculators" icon={<Icon name="calc" size={16} />} />
      </NavSection>
      <Box sx={{ flex: 1 }} />
      {mode && (
        <Box sx={{ px: '2px' }}>
          <Segmented
            block
            size="sm"
            label="Appearance"
            value={mode}
            onChange={() => undefined}
            options={[
              ['day', 'Day'],
              ['night', 'Night'],
            ]}
          />
        </Box>
      )}
      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '6px', pb: '2px' }}>
        <Avatar name="James Choe" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600 }}>James Choe</Box>
          <Meta>Advisor</Meta>
        </Box>
        <Icon name="gear" size={16} style={{ color: 'var(--tx-faint)' }} />
      </Box>
    </Box>
  );
};

const Finding = ({
  urgency,
  title,
  system,
  cost,
  note,
}: {
  urgency: keyof typeof URGENCY;
  title: string;
  system: string;
  cost: string;
  note: string;
}) => (
  <Box sx={{ display: 'flex', gap: '12px', py: '12px', '& + &': { borderTop: `1px solid ${tx.line}` } }}>
    <Box sx={{ width: 8, height: 8, mt: '6px', borderRadius: '50%', flex: 'none', backgroundColor: URGENCY[urgency] }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
        <Box sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{title}</Box>
        <Box sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{cost}</Box>
      </Box>
      <Meta sx={{ fontSize: 12 }}>{system}</Meta>
      <Box sx={{ mt: '6px', fontSize: 13, fontWeight: 500, lineHeight: 1.5, color: tx.muted }}>{note}</Box>
    </Box>
  </Box>
);

const Main = ({ paper, drop }: { paper?: boolean; drop: string }) => {
  const [view, setView] = useState('findings');
  return (
    <Box
      sx={panel({
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        p: '24px 28px',
        overflow: 'hidden',
        // paper on concrete: the solid sheet, not glass, with a warm soft drop
        ...(paper && {
          backgroundColor: tx.solid,
          boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, ${drop}`,
        }),
      })}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Eyebrow>Condition</Eyebrow>
          <Title as="h1">1234 Lita Place</Title>
          <Meta sx={{ fontSize: 12 }}>Inspected Oct 12 · 23 findings across 9 systems</Meta>
        </Box>
        <Button icon="download">Report</Button>
        <Button variant="primary">Request quotes</Button>
      </Box>

      <StatRow>
        <Stat value="72" label="Condition score" />
        <Stat value="$18,400" label="Est. repairs" />
        <Stat value="6" label="Urgent findings" />
        <Stat value="14 days" label="Inspection window" />
      </StatRow>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            ['findings', 'Findings'],
            ['systems', 'Systems'],
            ['document', 'Document'],
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <Chip on count={6}>
          Urgent
        </Chip>
        <Chip>Roof</Chip>
        <Chip>Plumbing</Chip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: '16px', minHeight: 0 }}>
        <Card title="Findings" aside="Sorted by urgency">
          <Box>
            <Finding
              urgency="now"
              title="Active leak at water heater"
              system="Plumbing · Garage"
              cost="$650–900"
              note="Corrosion at the T&P valve with water staining on the slab. Replace the valve and check the pan drain."
            />
            <Finding
              urgency="soon"
              title="Flashing gaps at chimney"
              system="Roof"
              cost="$1,200–1,800"
              note="Rust and gaps where the chimney meets the roof. Reseal before the monsoon season."
            />
            <Finding
              urgency="later"
              title="Double-tapped breaker"
              system="Electrical · Main panel"
              cost="$150–300"
              note="Two conductors on one breaker in slot 14. A licensed electrician should separate them."
            />
          </Box>
        </Card>
        <Card title="Inspection report" aside="p. 14 of 62">
          <Box
            sx={{
              fontSize: 13.5,
              fontWeight: 500,
              lineHeight: 1.65,
              color: tx.text,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
            <Box component="b" sx={{ fontSize: 14, fontWeight: 600 }}>
              4.3 Water heater
            </Box>
            <Box>
              The 50-gallon gas water heater (2011) is past its expected service life. Corrosion is visible at the
              temperature and pressure relief valve, and the slab beneath shows recent staining consistent with an
              active leak.
            </Box>
            <Box sx={{ color: tx.muted }}>
              Recommend evaluation by a licensed plumber prior to the end of the inspection period. Budget for
              replacement within 12 months.
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

const Side = ({ drop }: { drop: string }) => (
  <Box
    sx={panel({
      width: 320,
      flex: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      p: '18px',
      boxShadow: drop,
    })}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Segmented
        size="sm"
        value="ask"
        onChange={() => undefined}
        options={[
          ['ask', 'Ask Hauser'],
          ['chat', 'Client'],
          ['tasks', 'Tasks'],
        ]}
      />
      <Box sx={{ flex: 1 }} />
      <IconButton icon="x" label="Close" size="sm" variant="ghost" tipPlacement={false} />
    </Box>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: 13, fontWeight: 500 }}>
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '85%',
          p: '10px 12px',
          borderRadius: '14px',
          backgroundColor: tx.fill2,
          lineHeight: 1.5,
        }}>
        How urgent is the water heater?
      </Box>
      <Box sx={{ lineHeight: 1.6, color: tx.text }}>
        It&apos;s the most urgent item. The report notes an <b>active leak</b> at the relief valve (p. 14), so ask the
        seller to repair or credit it before your inspection window closes in 14 days.
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <Chip icon="book">Report p. 14</Chip>
        <Chip icon="dollar">$650–900</Chip>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: '8px' }}>
      <TextInput placeholder="Ask about this home" fullWidth />
      <IconButton icon="send" label="Send" tipPlacement={false} />
    </Box>
  </Box>
);

// A material: a base theme plus the few variables that make it its own (ink ramp, surfaces, selected well)
interface Material {
  base: ThemeName;
  vars?: Record<string, string>;
}
const material = (
  base: ThemeName,
  m: { rgb: string; text: string; muted: string; faint: string; panel: string; solid?: string; active?: string },
): Material => {
  const dark = base === 'dark';
  return {
    base,
    vars: {
      '--tx-ink-rgb': m.rgb,
      '--tx-text': m.text,
      '--tx-muted': m.muted,
      '--tx-faint': m.faint,
      '--tx-line': `rgba(${m.rgb}, ${dark ? 0.08 : 0.1})`,
      '--tx-fill': `rgba(${m.rgb}, ${dark ? 0.05 : 0.06})`,
      '--tx-fill-2': `rgba(${m.rgb}, ${dark ? 0.09 : 0.1})`,
      '--tx-fill-3': `rgba(${m.rgb}, ${dark ? 0.13 : 0.15})`,
      '--tx-glass-panel': m.panel,
      '--tx-solid': m.solid ?? m.panel,
      ...(m.active && { '--tx-active-bg': m.active }),
    },
  };
};

interface Direction {
  key: string;
  name: string;
  line: string;
  ground: string;
  light: string;
  pattern?: string;
  ink: string;
  tools: Material;
  content: Material;
  paper?: boolean;
  drop: string;
  mode?: 'day' | 'night';
}

const WARM_LIGHT =
  'radial-gradient(110% 85% at 14% -6%, rgba(255,255,255,0.28), transparent 62%), radial-gradient(90% 70% at 100% 108%, rgba(60,52,40,0.18), transparent 70%)';
const WARM_DROP = '0 18px 40px -18px rgba(60, 52, 40, 0.55)';

/* eslint-disable max-len */
const DIRECTIONS: Direction[] = [
  {
    key: 'materials',
    name: 'Day',
    line: 'chalk ground, slate tools, paper content',
    mode: 'day',
    ground: CHALK,
    light: WARM_LIGHT,
    ink: CHALK_INK_MUTED,
    tools: { base: 'dark' },
    content: { base: 'natural' },
    paper: true,
    drop: WARM_DROP,
  },
  {
    key: 'two-dark',
    name: 'Night',
    line: 'chalk ground, slate everywhere',
    mode: 'night',
    ground: CHALK,
    light: WARM_LIGHT,
    ink: CHALK_INK_MUTED,
    tools: { base: 'dark' },
    content: { base: 'dark' },
    drop: WARM_DROP,
  },
  {
    key: 'two-light',
    name: 'Two themes · Light',
    line: 'Natural throughout',
    ground: '#DBD8D3',
    light:
      'radial-gradient(110% 85% at 14% -6%, rgba(255,255,255,0.4), transparent 62%), radial-gradient(90% 70% at 100% 108%, rgba(55,55,54,0.08), transparent 70%)',
    ink: '#6E6A65',
    tools: { base: 'natural' },
    content: { base: 'natural' },
    drop: '0 18px 40px -22px rgba(40, 42, 46, 0.38)',
  },
  {
    // the product is called Blueprint: a drafting board under navy tools, the page on cool vellum
    key: 'blueprint',
    name: 'Blueprint',
    line: 'drafting-grid navy ground, navy tools, vellum content',
    ground: '#1A2532',
    light:
      'radial-gradient(110% 85% at 14% -6%, rgba(180,205,235,0.10), transparent 62%), radial-gradient(90% 70% at 100% 108%, rgba(0,0,0,0.30), transparent 70%)',
    pattern:
      'linear-gradient(rgba(170,196,226,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(170,196,226,0.07) 1px, transparent 1px), linear-gradient(rgba(170,196,226,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(170,196,226,0.035) 1px, transparent 1px)',
    ink: 'rgba(214, 226, 240, 0.6)',
    tools: material('dark', {
      rgb: '232, 239, 247',
      text: '#E8EFF7',
      muted: '#A6B5C6',
      faint: '#7C8DA1',
      panel: '#22303F',
      solid: '#2A3A4B',
    }),
    content: material('natural', {
      rgb: '28, 40, 54',
      text: '#1C2836',
      muted: '#566477',
      faint: '#7D8A99',
      panel: '#EDF1F4',
      active: '#DCE3EA',
    }),
    paper: true,
    drop: '0 24px 48px -24px rgba(0, 0, 0, 0.6)',
  },
  {
    // warm residential: a travertine floor, walnut joinery for the tools, cream stationery for the page
    key: 'travertine',
    name: 'Travertine',
    line: 'travertine ground, walnut tools, cream content',
    ground: '#D5C9B7',
    light: WARM_LIGHT,
    ink: 'rgba(70, 56, 44, 0.62)',
    tools: material('dark', {
      rgb: '245, 238, 229',
      text: '#F5EEE5',
      muted: '#C5B6A4',
      faint: '#9C8C7A',
      panel: '#3A2F28',
      solid: '#463930',
    }),
    content: material('natural', {
      rgb: '58, 47, 40',
      text: '#3A2F28',
      muted: '#786A5D',
      faint: '#9B8E80',
      panel: '#F7F2E9',
      active: '#E9E0D2',
    }),
    paper: true,
    drop: '0 18px 40px -18px rgba(70, 50, 30, 0.5)',
  },
  {
    // an architecture monograph: gallery white page, ink-black tools, pale concrete between
    key: 'monograph',
    name: 'Monograph',
    line: 'pale concrete ground, ink-black tools, gallery-white content',
    ground: '#E7E6E2',
    light:
      'radial-gradient(110% 85% at 14% -6%, rgba(255,255,255,0.5), transparent 62%), radial-gradient(90% 70% at 100% 108%, rgba(40,40,38,0.08), transparent 70%)',
    ink: 'rgba(30, 30, 28, 0.55)',
    tools: material('dark', {
      rgb: '246, 246, 244',
      text: '#F6F6F4',
      muted: '#A5A5A2',
      faint: '#7B7B78',
      panel: '#141414',
      solid: '#1E1E1E',
    }),
    content: material('light', {
      rgb: '18, 18, 18',
      text: '#121212',
      muted: '#5F5F5C',
      faint: '#8D8D8A',
      panel: '#FFFFFF',
      active: '#EDEDEB',
    }),
    paper: true,
    drop: '0 20px 44px -24px rgba(20, 20, 20, 0.35)',
  },
  {
    // the desert West, where Hauser started: sand ground, sage tools, bone page
    key: 'sage',
    name: 'Desert sage',
    line: 'sand ground, sage tools, bone content',
    ground: '#CDC2AE',
    light: WARM_LIGHT,
    ink: 'rgba(56, 60, 50, 0.62)',
    tools: material('dark', {
      rgb: '240, 242, 235',
      text: '#F0F2EB',
      muted: '#B7BFB0',
      faint: '#8D9585',
      panel: '#3C433A',
      solid: '#475045',
    }),
    content: material('natural', {
      rgb: '50, 54, 46',
      text: '#32362E',
      muted: '#6C7064',
      faint: '#8F9387',
      panel: '#F3EFE6',
      active: '#E4DED1',
    }),
    paper: true,
    drop: '0 18px 40px -18px rgba(60, 56, 40, 0.5)',
  },
];
/* eslint-enable max-len */

export const Frame = ({ d }: { d: Direction }) => (
  <Box
    data-frame={d.key}
    sx={{
      position: 'relative',
      width: 1440,
      height: 900,
      flex: 'none',
      overflow: 'hidden',
      backgroundColor: d.ground,
      backgroundImage: d.pattern ? `${d.light}, ${d.pattern}` : d.light,
      backgroundSize: d.pattern ? 'auto, auto, 120px 120px, 120px 120px, 24px 24px, 24px 24px' : undefined,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      p: '16px 20px 20px 0',
    }}>
    <Box sx={{ pl: '268px', fontSize: 12, fontWeight: 600, color: d.ink }}>
      {d.name}: {d.line}
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: '16px', alignItems: 'stretch' }}>
      <Themed name={d.tools.base} vars={d.tools.vars}>
        <Box sx={{ display: 'flex', '& > *': { borderTopLeftRadius: '0 !important', borderBottomLeftRadius: '0 !important' } }}>
          <Rail drop={d.drop} mode={d.mode} />
        </Box>
      </Themed>
      <Themed name={d.content.base} vars={d.content.vars}>
        <Main paper={d.paper} drop={d.drop} />
      </Themed>
      <Themed name={d.tools.base} vars={d.tools.vars}>
        <Side drop={d.drop} />
      </Themed>
    </Box>
    {/* the film grain, as in tokens.css, so the ground reads as a material */}
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.3, mixBlendMode: 'overlay', backgroundImage: GRAIN }} />
  </Box>
);

export const MaterialsCompare = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', p: '24px', alignItems: 'flex-start' }}>
    {DIRECTIONS.map((d) => (
      <Frame key={d.key} d={d} />
    ))}
  </Box>
);

export default MaterialsCompare;
